import moment from 'moment'
import React from 'react'
import { ActiveSlot } from '../../domain/ActiveSlot'
import { GraphEntity } from '../../domain/GraphEntity'
import dateFormatter from '../../helper/dateFormatter'
import Memory from '../../helper/memory'
import Percent from '../../helper/percent'
import { OperationsGraphEntity } from '../../redux/operations/OperationsState'
import Milliseconds from '../../helper/milliseconds'
import { layersNameToClass, layersNameToLabel, layersLayerExists } from '../../domain/Layers'

interface Props {
    activeSlot: ActiveSlot
    percentileLabel: string
    data: GraphEntity | OperationsGraphEntity
    downstreamEnabled: boolean
    showAverages: boolean
    parentSelector: string
    moveUp: number
    dataFixedTimezone?: string
    showMemory?: boolean
    showFailureDetails?: boolean
}
export default class Tooltip extends React.Component<Props> {
    public static defaultProps = {
        moveUp: 30,
        type: 'realtime',
    }

    public static dimensions = {
        width: 250,
    }

    public shouldComponentUpdate(nextProps, nextState, nextContext) {
        return !nextProps.activeSlot || !this.props.activeSlot || nextProps.activeSlot.timeIndex !== this.props.activeSlot.timeIndex
    }

    public updatePosition = () => {
        if (!this.props.activeSlot || !this.props.activeSlot.timeIndex) {
            return { display: false, positions: null }
        }

        if (jQuery(this.props.parentSelector).length === 0) {
            // @todo this is a hack for now, in order for this to display in storybook
            //       until I've found out how to make DOM selectors to work properly there
            return { display: true, positions: { left: 100, top: 100 } }
        }

        const offsetGraph = jQuery(this.props.parentSelector)[0].getBoundingClientRect()
        const { mouse } = this.props.activeSlot
        if (!mouse) {
            return { display: false, positions: null }
        }
        // padding sums from box + row
        const xCorrection = 35
        const yCorrection = 20

        const positions = {
            left: mouse.x + mouse.width / 2 - Tooltip.dimensions.width / 2 + xCorrection,
            top: offsetGraph.height - this.props.moveUp,
        }

        // do not run out of bounds to the left
        positions.left = Math.max(xCorrection, positions.left)

        // do not run out of bounds to the right
        if (positions.left + Tooltip.dimensions.width > offsetGraph.width) {
            positions.left = offsetGraph.width - Tooltip.dimensions.width
        }

        const viewportHeight = window.innerHeight
        const minimumTooltipHeight = 200
        const tooltipClipped = (offsetGraph.height + offsetGraph.y + minimumTooltipHeight) / (0.9 * viewportHeight) > 1.0

        if (tooltipClipped) {
            positions.top = mouse.y - offsetGraph.y + yCorrection
        }

        return {
            positions,
            display: true,
        }
    }

    public render() {
        const { display, positions } = this.updatePosition()

        if (!display || !this.props.activeSlot.timeIndex) {
            return null
        }

        const timezone = this.props.dataFixedTimezone || null

        const startDate = this.props.dataFixedTimezone
            ? moment(this.props.activeSlot.timeIndex).toDate()
            : moment(this.props.activeSlot.timeIndex + 'Z').toDate()

        const requestsKeys = Object.keys(this.props.data.report.requests)

        const bucketLengthInMinutes = moment.duration(moment(requestsKeys[1]).diff(moment(requestsKeys[0]))).asMinutes()

        const endDate =
            bucketLengthInMinutes > 1
                ? moment(startDate)
                      .add(bucketLengthInMinutes, 'minutes')
                      .toDate()
                : null

        const style: React.CSSProperties | undefined = positions
            ? {
                  width: Tooltip.dimensions.width,
                  position: 'absolute',
                  left: positions.left,
                  top: positions.top,
              }
            : undefined

        const report = this.props.data.report
        const activeSlot = this.props.activeSlot.timeIndex

        const percentileResponseTime = report.percentileResponseTimes[activeSlot]
        const averageResponseTime = report.averageResponseTimes[activeSlot]
        const errorCodes = report.errorCodes[activeSlot] || {}
        const requests = report.requests[activeSlot]
        const crawlers = report.crawlers[activeSlot]
        const errors = report.errors[activeSlot]
        const responseTimeTargetExceeded = report.responseTimeTargetExceeded[activeSlot]
        const memory = report.memory ? report.memory[activeSlot] : null
        const pageCacheHitRate = this.calculatePageCacheHitrate(report.pageCache, activeSlot)

        const downstream = report.downstream[activeSlot]
            ? Object.entries(report.downstream[activeSlot])
                  .filter(([service]) => layersLayerExists(service))
                  .map(([service, downstream]) => {
                      let percent = downstream.requests / requests
                      const weightedAverage = Math.max(1, Math.ceil(downstream.average * percent))
                      percent = Math.round(percent * 100)

                      let textClass = ''
                      if (weightedAverage === 1) {
                          textClass = 'text-mute'
                      } else if (weightedAverage / averageResponseTime >= 0.3) {
                          textClass = 'text-bold'
                      }

                      return {
                          ...downstream,
                          service,
                          percent,
                          weightedAverage: weightedAverage,
                          textClass,
                      }
                  })
                  .sort((a, b) => b.weightedAverage - a.weightedAverage)
            : []

        const downstreamInsignificantServices = downstream
            .filter(downstream => downstream.weightedAverage <= 1)
            .map(downstream => layersNameToLabel(downstream.service))

        return (
            <div className="infoBoxHtml row" style={style}>
                <div className="time text-ellipsis">
                    {!moment().isSame(startDate, 'day') ? (
                        <span className="text-muted"> {moment(startDate).format('DD.MM.YYYY')} </span>
                    ) : null}
                    {endDate ? dateFormatter(startDate) + ' - ' + dateFormatter(endDate) : dateFormatter(startDate)}{' '}
                    {timezone ? '(' + timezone + ')' : null}
                </div>
                <div>
                    {percentileResponseTime > 60000 ? (
                        <>
                            <div className="row-flex">
                                <div className="label">
                                    <i className="fa fa-exclamation-triangle text-purple" aria-hidden="true" /> {this.props.percentileLabel}
                                </div>
                                <div className="value"> 60+</div>
                            </div>
                            <div className="alert alert-purple" style={{ margin: 0, padding: '0.25em', marginBottom: '0.5em' }}>
                                <p>
                                    <small>Measured response time exceeds maximum of 60 seconds</small>
                                </p>
                            </div>
                        </>
                    ) : (
                        <div className="row-flex">
                            <div className="label">
                                <i className="icon fa fa-square layer-requests" /> {this.props.percentileLabel}
                            </div>
                            <div className="value">{percentileResponseTime} ms</div>
                        </div>
                    )}
                </div>
                {this.props.showMemory && memory ? (
                    <div className="row-flex">
                        <div className="label">
                            <i className="icon fa fa-square unit memory" /> Max Memory
                        </div>
                        <div className="value">
                            <Memory value={memory * 1024} class="text" boldUnit={false} />
                        </div>
                    </div>
                ) : null}
                {pageCacheHitRate > 0 ? (
                    <div className="row-flex">
                        <div className="label">
                            <i className="icon fa fa-square unit requests" /> Page Cache Hit Rate
                        </div>
                        <div className="value">
                            <Percent class="tw-font-weight-normal" value={pageCacheHitRate} />
                        </div>
                    </div>
                ) : null}
                <div className="row-flex">
                    <div className="label">
                        <i className="icon fa fa-ellipsis-h" /> Requests
                    </div>
                    <div className="value">{requests}</div>
                </div>
                {crawlers ? (
                    <div className="row-flex">
                        <div className="label">
                            <i className="icon fa fa-square layer-crawlers" /> Crawlers
                        </div>
                        <div className="value">{crawlers}</div>
                    </div>
                ) : null}
                {!this.props.showFailureDetails ? (
                    <div className="row-flex">
                        <div className="label">
                            <i className="icon fa fa-square layer-errors" /> Failures
                        </div>
                        <div className="value">{errors}</div>
                    </div>
                ) : null}

                {this.props.showFailureDetails ? (
                    <div>
                        <div className="row-flex tw-mt-4">
                            <strong>Failures</strong>
                        </div>
                        <div className="row-flex">
                            <div className="label">
                                <i className="icon fa fa-square layer-errors" /> Total
                            </div>
                            <div className="value">{errors}</div>
                        </div>
                        <div className="row-flex">
                            <div className="label">
                                <i className="icon fa fa-square layer-errors" /> Slow Requests
                            </div>
                            <div className="value">{responseTimeTargetExceeded}</div>
                        </div>
                    </div>
                ) : null}

                {this.props.data.report.responseTimeTarget ? (
                    <div className="row-flex">
                        <div className="label">
                            <i className="icon fa fa-ellipsis-h layer-response-time-target" /> Response time target
                        </div>
                        <div className="value">
                            <Milliseconds value={this.props.data.report.responseTimeTarget * 1000} />
                        </div>
                    </div>
                ) : null}

                {!this.props.showAverages ? null : (
                    <div>
                        <div className="row-flex tw-mt-4">
                            <strong>Averages</strong>
                        </div>
                        <div className="row-flex">
                            <div className="label">
                                <i className="icon fa fa-square layer-remainder" /> Total
                            </div>
                            <div className="value">{averageResponseTime} ms</div>
                        </div>
                    </div>
                )}

                {!this.props.downstreamEnabled || !downstream ? null : (
                    <>
                        {downstream
                            .filter(downstream => downstream.weightedAverage > 1)
                            .map(downstream => (
                                <div className={'row-flex ' + downstream.textClass} key={downstream.service}>
                                    <div className={'label ' + downstream.textClass}>
                                        <i className={'icon fa fa-square ' + layersNameToClass(downstream.service)} />{' '}
                                        {layersNameToLabel(downstream.service)} ({downstream.percent}% of reqs)
                                    </div>
                                    <div className="value">
                                        {downstream.weightedAverage === 1 ? '≤' + downstream.weightedAverage : downstream.weightedAverage}{' '}
                                        ms
                                    </div>
                                </div>
                            ))}
                        {downstreamInsignificantServices.length > 0 ? (
                            <div className="row-flex text-mute">
                                <div className="label text-mute">
                                    <i className="icon fa fa-square layer-unknown" /> {downstreamInsignificantServices.join(', ')}
                                </div>
                                <div className="value">≤1 ms</div>
                            </div>
                        ) : null}
                    </>
                )}

                {Object.keys(errorCodes).length > 0 ? (
                    <div>
                        <div className="row-flex tw-mt-4">
                            <strong>Http Status Codes</strong>
                        </div>
                        {Object.keys(errorCodes).map((code: string) => {
                            const renderedCode = code
                                .replace('199', '1xx')
                                .replace('299', '2xx')
                                .replace('399', '3xx')
                                .replace('499', '4xx')
                                .replace('599', '5xx')

                            return (
                                <div className="row-flex" key={code}>
                                    <div className="label">{renderedCode}:</div>
                                    <div className="value">{errorCodes[code]}</div>
                                </div>
                            )
                        })}
                    </div>
                ) : null}
            </div>
        )
    }

    private calculatePageCacheHitrate = (
        pageCache: { [timeslot: string]: { hits: number; misses: number } } | undefined,
        timeSlot: string,
    ): number => {
        if (!pageCache || !pageCache[timeSlot]) {
            return 0
        }

        const { hits, misses } = pageCache[timeSlot]

        if (hits === 0 || misses === 0) {
            return 0
        }

        return hits / (hits + misses)
    }
}
